import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigation } from "@/context/NavigationContext";
import { Plane, Loader2, KeyRound, ShieldAlert, CheckCircle2, Lock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { AppService } from "@/services/appService";
import { message } from "antd";

const Login = () => {
  const { login, currentLanguageDictionary } = useAuth();
  const { navigateTo } = useNavigation();
  const [identifier, setIdentifier] = useState<string>(""); // Email sau username
  const [password, setPassword] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false); // Stare pentru evidențiere eroare pe inputs

  // Stări pentru gestionarea modalului de recuperare
  const [isForgotOpen, setIsForgotOpen] = useState<boolean>(false);
  const [isSendingForgot, setIsForgotLoading] = useState<boolean>(false);

  // Funcție simplă care extrage exclusiv valoarea asociată cheii din dicționar
  const getDictionaryValue = (key: string): string => {
    if (!currentLanguageDictionary) return "";
    const item = currentLanguageDictionary.find((d: any) => d.dictKey === key);
    return item?.dictValue || "";
  };

  // Maparea cheilor pentru ecranul principal de Login
  const titleText = getDictionaryValue("login.title");
  const descriptionText = getDictionaryValue("login.description");
  const usernameLabel = getDictionaryValue("login.username.txb");
  const usernamePlaceholder = getDictionaryValue("login.username.placeholder");
  const passwordLabel = getDictionaryValue("login.password.txb");
  const buttonLoginText = getDictionaryValue("login.button.login");
  const forgotPasswordText = getDictionaryValue("login.button.forgot.password");
  const noAccountText = getDictionaryValue("login.no.account");
  const buttonRegisterText = getDictionaryValue("login.button.register");

  // Maparea cheilor pentru starea de încărcare/mesaje
  const connectingText = getDictionaryValue("login.loading.connecting");
  const sendingText = getDictionaryValue("login.loading.sending");
  const warningForgotPassword = getDictionaryValue("login.forgot.warning");

  // Maparea cheilor pentru Modalul de Recuperare Parolă
  const forgotHeaderText = getDictionaryValue("login.forgot.header");
  const forgotTitleText = getDictionaryValue("login.forgot.title");
  const forgotDescText = getDictionaryValue("login.forgot.description");
  const forgotNextStepsTitle = getDictionaryValue("login.forgot.steps.title");
  const forgotStep1 = getDictionaryValue("login.forgot.step1");
  const forgotStep2 = getDictionaryValue("login.forgot.step2");
  const forgotButtonSubmit = getDictionaryValue("login.forgot.button.submit");
  const forgotButtonCancel = getDictionaryValue("login.forgot.button.cancel");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading) return;

    setIsLoading(true);
    setIsError(false);

    try {
      const isSuccess = await login(identifier, password);

      if (isSuccess) {
        navigateTo("home"); // 🟢 Redirecționează către 'home'
      } else {
        setIsError(true); // 🔴 Activează conturul roșu pe câmpuri
      }
    } catch (error) {
      console.error("Login failed", error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordTrigger = async () => {
    if (!identifier || identifier.trim() === "") {
      message.warning({
        content: warningForgotPassword,
        duration: 4
      });
      return;
    }
    setIsForgotOpen(true);
  };

  const handleConfirmReset = async () => {
    setIsForgotLoading(true);
    try {
      const success = await AppService.forgotPassword(identifier);
      if (success) {
        setIsForgotOpen(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsForgotLoading(false);
    }
  };

  return (
    <div className="min-h-[85vh] flex items-center justify-center p-6 animate-fade-in relative bg-black/40 backdrop-blur-[2px]">

      {/* CARDUL PRINCIPAL DE LOGIN (Stilizat identic cu Alertele) */}
      <div className="relative w-full max-w-md overflow-hidden rounded-[2.5rem] bg-[#0F172A] border border-orange-500/30 shadow-[0_0_50px_rgba(249,115,22,0.15)]">

        {/* Header Decorativ Gradient */}
        <div className="h-2 bg-gradient-to-r from-orange-600 via-yellow-500 to-orange-600" />

        <div className="p-10">

          {/* Header ID/Aesthetic bloc */}
          <div className="flex items-center gap-5 mb-8">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-500/10 text-orange-500 border border-orange-500/20 shadow-[0_0_20px_rgba(249,115,22,0.2)]">
              <Plane size={36} className="rotate-45" />
            </div>
            <div>
              <p className="text-[10px] font-black tracking-[0.3em] text-orange-500 uppercase">
                HangarX Portal
              </p>
              <h3 className="text-2xl font-black text-white italic leading-tight">
                {titleText}
              </h3>
            </div>
          </div>

          {/* Scurtă descriere interfață */}
          <p className="text-slate-400 text-xs leading-relaxed mb-8">
            {descriptionText}
          </p>

          {/* Formularul de Date */}
          <form onSubmit={handleSubmit} className="space-y-5 mb-8">
            <div className="space-y-2">
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block pl-1">
                {usernameLabel}
              </label>
              <input
                type="text"
                required
                value={identifier}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setIdentifier(e.target.value);
                  if (isError) setIsError(false);
                }}
                className={`w-full h-12 px-4 rounded-xl bg-slate-950/60 border text-white text-sm focus:outline-none transition-all placeholder:text-slate-600 ${
                  isError
                    ? "border-red-500/80 focus:border-red-500 focus:ring-1 focus:ring-red-500/40 shadow-[0_0_15px_rgba(239,68,68,0.2)]"
                    : "border-orange-500/10 focus:border-orange-500/40 focus:ring-1 focus:ring-orange-500/20"
                }`}
                placeholder={usernamePlaceholder}
              />
            </div>

            <div className="space-y-2">
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block pl-1">
                {passwordLabel}
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPassword(e.target.value);
                  if (isError) setIsError(false);
                }}
                className={`w-full h-12 px-4 rounded-xl bg-slate-950/60 border text-white text-sm focus:outline-none transition-all placeholder:text-slate-700 ${
                  isError
                    ? "border-red-500/80 focus:border-red-500 focus:ring-1 focus:ring-red-500/40 shadow-[0_0_15px_rgba(239,68,68,0.2)]"
                    : "border-orange-500/10 focus:border-orange-500/40 focus:ring-1 focus:ring-orange-500/20"
                }`}
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-4 mt-2 rounded-xl bg-orange-600 text-white text-xs font-black uppercase tracking-widest hover:bg-orange-500 disabled:opacity-50 transition-all shadow-[0_0_20px_rgba(249,115,22,0.3)] flex items-center justify-center gap-3 group"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {connectingText}
                </>
              ) : (
                <>
                  <Lock size={14} className="group-hover:-translate-y-0.5 transition-transform" />
                  {buttonLoginText}
                </>
              )}
            </button>
          </form>

          {/* Zona inferioară de acțiuni alternative */}
          <div className="flex flex-col gap-3 pt-6 border-t border-slate-500/10 text-center">
            <button
              type="button"
              onClick={handleForgotPasswordTrigger}
              className="text-orange-500 hover:text-orange-400 text-xs font-bold tracking-wide transition-colors focus:outline-none"
            >
              {forgotPasswordText}
            </button>

            <div className="text-slate-500 text-[11px] font-semibold mt-1">
              {noAccountText}{" "}
              <button
                type="button"
                onClick={() => navigateTo("register")}
                className="text-white hover:underline font-bold ml-1"
              >
                {buttonRegisterText}
              </button>
            </div>
          </div>

        </div>

        {/* Brand Vector HUD decorativ pe fundal */}
        <div className="absolute -bottom-16 -right-16 opacity-[0.02] pointer-events-none text-white">
          <Plane size={240} className="rotate-45" />
        </div>
      </div>

      {/* 🔴 MODAL SECURE FORGOT PASSWORD RECOVERY */}
      <AnimatePresence>
        {isForgotOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-[6px] p-6">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-md overflow-hidden rounded-[2.5rem] bg-[#0F172A] border border-red-500/30 shadow-[0_0_50px_rgba(239,68,68,0.15)]"
            >
              <div className="h-2 bg-gradient-to-r from-red-600 via-orange-500 to-red-600" />

              <div className="p-10">
                <div className="flex items-center gap-5 mb-8">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-500/10 text-red-500 border border-red-500/20 shadow-[0_0_20px_rgba(239,68,68,0.2)]">
                    <KeyRound size={32} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black tracking-[0.3em] text-red-500 uppercase">
                      {forgotHeaderText}
                    </p>
                    <h3 className="text-2xl font-black text-white italic leading-tight">
                      {forgotTitleText}
                    </h3>
                  </div>
                </div>

                <div className="space-y-5 mb-10">
                  <p className="text-slate-300 text-sm leading-relaxed">
                    {forgotDescText}
                    <span className="block mt-2 font-mono text-xs bg-black/40 border border-white/5 rounded-lg p-2.5 text-red-400 font-bold truncate">
                      {identifier}
                    </span>
                  </p>

                  <div className="rounded-2xl bg-red-500/5 border border-red-500/10 p-5">
                    <h4 className="text-xs font-bold text-white uppercase tracking-wider mb-3">{forgotNextStepsTitle}</h4>
                    <ul className="space-y-3.5">
                      <li className="flex gap-3 text-xs text-slate-400 leading-tight">
                        <CheckCircle2 size={14} className="text-red-500 shrink-0 mt-0.5" />
                        <span>{forgotStep1}</span>
                      </li>
                      <li className="flex gap-3 text-xs text-slate-400 leading-tight">
                        <CheckCircle2 size={14} className="text-red-500 shrink-0 mt-0.5" />
                        <span>{forgotStep2}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={handleConfirmReset}
                    disabled={isSendingForgot}
                    className="w-full px-6 py-4 rounded-xl bg-red-600 text-white text-xs font-black uppercase tracking-widest hover:bg-red-700 disabled:opacity-50 transition-all shadow-[0_0_20px_rgba(239,68,68,0.3)] flex items-center justify-center gap-3 group"
                  >
                    {isSendingForgot ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {sendingText}
                      </>
                    ) : (
                      forgotButtonSubmit
                    )}
                  </button>
                  <button
                    onClick={() => setIsForgotOpen(false)}
                    disabled={isSendingForgot}
                    className="w-full px-6 py-3 rounded-xl text-slate-500 text-[10px] font-black uppercase tracking-widest hover:text-slate-300 transition-colors disabled:opacity-30"
                  >
                    {forgotButtonCancel}
                  </button>
                </div>
              </div>

              <div className="absolute -bottom-16 -right-16 opacity-[0.02] pointer-events-none text-white">
                <ShieldAlert size={240} />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Login;